import { Component, OnDestroy, OnInit } from '@angular/core';
import { DbDocument, Faktura, FakturaWDT, DocumentHelper, TemplateUtils } from 'fakturnia-shared';
import { FakturaKoncowa } from 'fakturnia-shared';
import { FakturaKorygujaca } from 'fakturnia-shared';
import { FakturaKorygujacaZaliczkowa } from 'fakturnia-shared';
import { FakturaKorygujacaRozliczeniowa } from 'fakturnia-shared';
import { FakturaRozliczeniowa } from 'fakturnia-shared';
import { FakturaMPP } from 'fakturnia-shared';
import { FakturaVAT } from 'fakturnia-shared';
import { FakturaVATMarza } from 'fakturnia-shared';
import { FakturaZaliczka } from 'fakturnia-shared';
import { DocumentsService } from 'shared/services/documents.service';
import { Utils } from 'fakturnia-shared';
import { CommonModule } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DataSynchronizerService } from 'shared/services/data-synchronization.service';
import { Subscription } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { DateTime } from 'luxon';
import { DirectionBadgeComponent } from '../direction-badge/direction-badge.component';

@Component({
  standalone: true,
  imports: [CommonModule, MatMenuModule, MatButtonModule, MatIconModule, MatTooltipModule, RouterLink, DirectionBadgeComponent],
  selector: 'invoice-card',
  templateUrl: './invoice-card.component.html',
  styleUrls: ['./invoice-card.component.scss']
})
export class InvoiceCardComponent implements OnInit, OnDestroy {

  private _subscriptions: Subscription[] = []
  Object = Object
  countFieldOptions: { value: 'vatSum' | 'netSum' | 'grossSum', text: string }[] = [
    { value: 'netSum', text: 'Wartość netto' },
    { value: 'vatSum', text: 'Kwota VAT' },
    { value: 'grossSum', text: 'Wartość brutto' },
  ]

  selectedFieldId = this.countFieldOptions[2]
  range = 12
  data: MonthYearDocuments[] = []
  visibleData: MonthYearDocuments[] = []
  recordsPerPage = 12
  currentPage = 1
  hasMoreRecords = true
  docsCount = 0

  constructor(
    private _documentsService: DocumentsService,
    private _dataSynchronizerService: DataSynchronizerService
  ) {
    this._initialize()
  }

  public toDecimal(value) {
    return Utils.toDecimal(value)
  }

  /**
   * Pobiera prawidłową wartość dokumentu
   * Dla dokumentów KSeF używa grossSum z dokumentu (zapisane przy synchronizacji)
   * Dla zaliczek używa advancePaymentAmount
   */
  public getDocumentValue(document: DbDocument): number {
    const field = this.selectedFieldId.value; // 'grossSum' | 'netSum' | 'vatSum'

    // Dla FakturaZaliczka z zamówieniem — kwota zaliczki, nie łączna wartość zamówienia
    // (grossSum w DB = wartość zamówienia, advancePaymentAmount = kwota zaliczki)
    if (document.documentType === 'FakturaZaliczka' && (document as any).linkedOrderId && (document as any).advancePaymentAmount !== undefined) {
      if (field === 'grossSum') return (document as any).advancePaymentAmount;
      const breakdown = (document as any).advancePaymentTaxBreakdown || [];
      if (field === 'netSum') return breakdown.reduce((sum: number, row: any) => sum + (row.netValue || 0), 0);
      if (field === 'vatSum') return breakdown.reduce((sum: number, row: any) => sum + (row.vatValue || 0), 0);
      return (document as any).advancePaymentAmount;
    }

    // Wartość z DB — działa dla wszystkich typów:
    // count() oblicza różnice dla korekt (FakturaKorygujaca, KOR_ZAL), kwoty dla zwykłych
    const value = (document as any)[field];
    if (value != null) return value;

    // Fallback: grossSum zawsze istnieje
    if (field === 'grossSum') return document.grossSum || 0;

    // Fallback KSeF: wyciągnij z ksefRawData
    if (document.source === 'ksef') {
      const rawData = (document as any).ksefRawData;
      if (rawData?.Fa) {
        if (field === 'netSum') return this._extractKsefNetSum(rawData.Fa);
        if (field === 'vatSum') return this._extractKsefVatSum(rawData.Fa);
      }
    }

    // Fallback: FakturaKorygujaca — oblicz różnicę z produktów
    if (document.documentType === 'FakturaKorygujaca') {
      return this._calculateCorrectionValue(document);
    }

    // Fallback: oblicz z produktów
    if (document.products?.length) {
      const productField = field === 'netSum' ? 'netValue' : 'amountOfVat';
      return document.products.reduce((sum: number, p: any) => sum + (p[productField] || 0), 0);
    }

    // Ostateczny fallback: proporcja z grossSum
    const gross = document.grossSum || 0;
    if (field === 'netSum') return gross / 1.23;
    return gross - gross / 1.23;
  }

  /**
   * Wyciąga sumę netto z danych KSeF (P_13_1 do P_13_11)
   */
  private _extractKsefNetSum(fa: any): number {
    let sum = 0;
    for (let i = 1; i <= 11; i++) {
      const key = `P_13_${i}`;
      if (fa[key]) sum += parseFloat(fa[key]) || 0;
    }
    return sum;
  }

  /**
   * Wyciąga sumę VAT z danych KSeF (P_14_1 do P_14_5)
   */
  private _extractKsefVatSum(fa: any): number {
    let sum = 0;
    for (let i = 1; i <= 5; i++) {
      const key = `P_14_${i}`;
      if (fa[key]) sum += parseFloat(fa[key]) || 0;
    }
    return sum;
  }

  /**
   * Oblicza wartość z produktów dokumentu.
   * Dla FakturaKorygujaca: oblicza różnicę (po korekcie - przed korektą).
   */
  /**
   * Oblicza wartość korekty jako różnicę (po korekcie - przed korektą).
   * Fallback dla starych FakturaKorygujaca bez netSum/vatSum w DB.
   */
  private _calculateCorrectionValue(document: DbDocument): number {
    const field = this.selectedFieldId.value === 'grossSum' ? 'grossValue'
      : this.selectedFieldId.value === 'netSum' ? 'netValue'
      : 'amountOfVat';

    // Suma pozycji korekty (= wartości PO korekcie)
    let afterSum = 0;
    document.products.forEach(product => {
      afterSum += product[field] || 0;
    });

    // Znajdź fakturę korygowaną i oblicz wartości PRZED korektą
    let beforeSum = 0;
    if (document.correctedInvoiceId) {
      const correctedDoc = this._documentsService.getById(document.correctedInvoiceId);
      if (correctedDoc?.products?.length) {
        correctedDoc.products.forEach(product => {
          beforeSum += product[field] || 0;
        });
      }
    }

    return afterSum - beforeSum;
  }

  /**
   * Pobiera nazwę typu dokumentu do wyświetlenia
   */
  public getDocumentTypeName(document: DbDocument): string {
    if (!document.documentType) return '';
    const template = TemplateUtils.GetDocumentById(document.documentType);
    return template?.name || document.documentType;
  }

  /**
   * Pobiera nazwę nabywcy
   */
  public getBuyerName(document: DbDocument): string {
    if (!document.buyer) return 'Brak nabywcy';
    if (document.buyer.type === 'company') {
      return document.buyer.name || 'Brak nazwy';
    }
    if (document.buyer.type === 'person') {
      return `${document.buyer.firstName || ''} ${document.buyer.lastName || ''}`.trim() || 'Brak danych';
    }
    // Dla dokumentów KSeF może być tylko name
    return document.buyer.name || 'Brak nabywcy';
  }

  /**
   * Pobiera walutę dokumentu - dla KSeF może być w różnych miejscach
   */
  public getDocumentCurrency(document: DbDocument): string {
    // Najpierw sprawdź bezpośrednio na dokumencie
    if (document.currency) {
      return document.currency;
    }
    // Dla KSeF sprawdź w ksefRawData
    const rawData = (document as any).ksefRawData;
    if (rawData?.currency) {
      return rawData.currency;
    }
    if (rawData?.Fa?.KodWaluty) {
      return rawData.Fa.KodWaluty;
    }
    return 'PLN';
  }

  public loadMore() {
    this.currentPage++;
    const startIndex = (this.currentPage - 1) * this.recordsPerPage;
    const endIndex = this.currentPage * this.recordsPerPage;
    const newRecords = this.data.slice(startIndex, endIndex);
    this.visibleData = [...this.visibleData, ...newRecords];
    this._hasMoreRecords()
  }

  public changeOption(field) {
    this.selectedFieldId = field
    this._initialize()
  }

  private _initialize() {
    this._buildRange()
    this._addDocuments()
    this._updateVisibleData()
    this._hasMoreRecords()
  }

  private _hasMoreRecords() {
    this.hasMoreRecords = this.currentPage * this.recordsPerPage < this.data.length;
  }

  private _updateVisibleData() {
    const startIndex = (this.currentPage - 1) * this.recordsPerPage;
    const endIndex = this.currentPage * this.recordsPerPage;
    this.visibleData = this.data.slice(startIndex, endIndex);
  }

  private _buildRange() {
    this.data = [];
    const currentDate: DateTime = DateTime.now()
    let months = 12

    const firstInvoice: any = this._documentsService.getOldestInvoice();
    if (firstInvoice) {
      const firstInvoiceDate = DateTime.fromISO(firstInvoice.issueDate);
      months = Math.ceil(currentDate.diff(firstInvoiceDate, 'months').months);
      if (months < 12) months = 12;
    }

    let currentDatePointer = currentDate.startOf('month');

    for (let i = 0; i <= months; i++) {
      this.data.push(
        new MonthYearDocuments({
          month: currentDatePointer.month,
          year: currentDatePointer.year,
          countFieldId: this.selectedFieldId.value,
          getDocumentValue: (doc) => this.getDocumentValue(doc),
          getDocumentCurrency: (doc) => this.getDocumentCurrency(doc)
        })
      );
      currentDatePointer = currentDatePointer.minus({ months: 1 });
    }

    if (this.data[0]) this.data[0].expanded = true
  }

  private _addDocuments() {
    const invoiceTypes = [
      new Faktura(),
      new FakturaWDT(),
      new FakturaKoncowa(),
      new FakturaKorygujaca(),
      new FakturaKorygujacaZaliczkowa(),
      new FakturaKorygujacaRozliczeniowa(),
      new FakturaRozliczeniowa(),
      new FakturaMPP(),
      new FakturaVATMarza(),
      new FakturaVAT(),
      new FakturaZaliczka()
    ].map(x => x['id'])

    this.data = this.data.filter((element: MonthYearDocuments) => {
      const issuedDocuments = this._documentsService.getDocumentsByMonthAndYear(invoiceTypes, element.month, element.year);

      // Filtruj tylko dokumenty z direction = 'income' (przychody, nie koszty)
      const incomeDocuments = issuedDocuments.filter((document: DbDocument) =>
        DocumentHelper.getDirection(document) === 'income'
      );

      if (incomeDocuments.length > 0) {
        incomeDocuments.forEach((document: DbDocument) => {
          element.addDocument(document);
        });

        element.documents.sort((a: DbDocument, b: DbDocument) => {
          const dateA = new Date(a.issueDate);
          const dateB = new Date(b.issueDate);

          if (dateA.getFullYear() !== dateB.getFullYear()) {
            return dateB.getFullYear() - dateA.getFullYear();
          }

          if (dateA.getMonth() !== dateB.getMonth()) {
            return dateB.getMonth() - dateA.getMonth();
          }

          return parseInt(b.documentNo?.toString() || '0') - parseInt(a.documentNo?.toString() || '0');
        });

        return true;
      }

      return false;
    });
  }

  ngOnInit(): void {
    this._subscriptions.push(
      this._dataSynchronizerService.currentData.subscribe({
        next: (data) => {
          if (!data) return
          if (data.table == 'documents') {
            this._initialize()
          }
        }
      }))
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(sub => sub.unsubscribe())
  }

}

export class CurrencySummary {
  currency: string
  summary: number
}

/**
 * Klasa grupująca dokumenty wg miesiąca/roku
 * Działa bezpośrednio z DbDocument (nie wymaga Invoice)
 */
export class MonthYearDocuments {

  day: number | undefined
  month: number
  monthName: string
  year: number
  documents: DbDocument[] = []
  expanded: boolean = false
  sum = {}
  summary: CurrencySummary[] = []
  countFieldId: 'vatSum' | 'netSum' | 'grossSum' = 'grossSum'
  private _getDocumentValue: (doc: DbDocument) => number
  private _getDocumentCurrency: (doc: DbDocument) => string

  constructor({ day, month, year, countFieldId, getDocumentValue, getDocumentCurrency }: {
    day?: number,
    month: number,
    year: number,
    countFieldId: 'vatSum' | 'netSum' | 'grossSum',
    getDocumentValue: (doc: DbDocument) => number,
    getDocumentCurrency: (doc: DbDocument) => string
  }) {
    this.day = day
    this.month = month
    this.monthName = DateTime.fromObject({ year: year, month: month }).setLocale('pl').toFormat('LLLL')
    this.year = year
    this.countFieldId = countFieldId
    this._getDocumentValue = getDocumentValue
    this._getDocumentCurrency = getDocumentCurrency
  }

  public toggleExpand() {
    this.expanded = !this.expanded
  }

  public addDocument(document: DbDocument) {
    this.documents.push(document)
    this._count()
  }

  public setCountField(fieldId: 'vatSum' | 'netSum' | 'grossSum') {
    this.countFieldId = fieldId
    this._count()
  }

  private _count() {
    this.sum = {}
    if (this.documents.length == 0) return

    this.documents.forEach((document: DbDocument) => {
      const value = this._getDocumentValue(document);
      const currency = this._getDocumentCurrency(document);

      if (typeof this.sum[currency] == 'undefined') this.sum[currency] = 0
      this.sum[currency] += value || 0
    })

    this.summary = Object.keys(this.sum).map(key => { return { currency: key, summary: this.sum[key] } })
  }
}

/**
 * Alias dla kompatybilności wstecznej z income-chart
 */
export class MonthYearInvoices {
  day: number | undefined
  month: number
  monthName: string
  year: number
  invoices: any[] = []
  expanded: boolean = false
  sum = {}
  summary: CurrencySummary[] = []
  countFieldId: 'vatSum' | 'netSum' | 'grossSum' = 'grossSum'

  constructor({ day, month, year, countFieldId }: { day?: number, month: number, year: number, countFieldId: 'vatSum' | 'netSum' | 'grossSum' }) {
    this.day = day
    this.month = month
    this.monthName = DateTime.fromObject({ year: year, month: month }).setLocale('pl').toFormat('LLLL')
    this.year = year
    this.countFieldId = countFieldId
  }

  public toggleExpand() {
    this.expanded = !this.expanded
  }

  public addInvoice(invoice: any) {
    this.invoices.push(invoice)
    this._count()
  }

  public setCountField(fieldId: 'vatSum' | 'netSum' | 'grossSum') {
    this.countFieldId = fieldId
    this._count()
  }

  private _count() {
    this.sum = {}
    if (this.invoices.length == 0) return

    this.invoices.forEach((invoice: any) => {
      let value: number;

      // Dla FakturaZaliczka z zamówieniem - specjalna obsługa
      if (invoice.documentType === 'FakturaZaliczka' && invoice.linkedOrder && invoice.advancePaymentAmount !== undefined) {
        if (this.countFieldId === 'grossSum') {
          value = invoice.advancePaymentAmount;
        } else if (this.countFieldId === 'netSum') {
          value = (invoice.advancePaymentTaxBreakdown || []).reduce((sum, row) => sum + (row.netValue || 0), 0);
        } else if (this.countFieldId === 'vatSum') {
          value = (invoice.advancePaymentTaxBreakdown || []).reduce((sum, row) => sum + (row.vatValue || 0), 0);
        } else {
          value = invoice.advancePaymentAmount;
        }
      } else {
        // Dla wszystkich innych dokumentów (w tym KSeF) - użyj wartości z odpowiedniego pola
        value = invoice[this.countFieldId];
      }

      if (typeof value == 'undefined' || value == null) {
        console.warn(`[MonthYearInvoices]: Invalid value for ${this.countFieldId}:`, value)
        value = 0;
      }

      if (typeof this.sum[invoice.currency] == 'undefined') this.sum[invoice.currency] = 0
      this.sum[invoice.currency] += value || 0
    })

    this.summary = Object.keys(this.sum).map(key => { return { currency: key, summary: this.sum[key] } })
  }
}
